
import { ApplicationUserModel, LocationModel, LocationGroupTypeEnum } from '@/libs/Api';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import Page from '@/Page.vue';
import { Component, Ref } from 'vue-property-decorator';
import { applicationUserStore } from '@/libs/user-settings/+state/store';
import { Guid } from '@/libs/common/functions/guid';
import { locationsStore } from '@/libs/locations/+state/store';
import GroupUserList from '@/libs/settings/components/GroupUserList.vue';
import LocationGroupList from '@/libs/settings/components/LocationGroupList.vue';

@Component({
    components: { LocationGroupList, GroupUserList },
})
export default class LocationDialog extends Page {
    id!: string;
    dialog = false;
    valid = true;
    saving = false;

    loading = false;
    applicationUser: ApplicationUserModel = applicationUserStore.useGetter(CrudGetter.Detail) as ApplicationUserModel;
    location: LocationModel | null = null;
    deleteDialog = false;
    deleting = false;
    LocationGroupTypeEnum: typeof LocationGroupTypeEnum = LocationGroupTypeEnum;

    @Ref() form!: any;

    inheritMaintenance = true;
    inheritDowntime = true;
    inheritLostProfit = true;

    get isNewItem(): boolean {
        return this.location?.id == Guid.EmptyGuid();
    }

    open(id: string) {
        this.dialog = true;
        this.id = id;
        this.load();
    }

    close() {
        this.dialog = false;
    }

    deleteClick() {
        this.deleteDialog = true;
    }

    deleteLocation() {
        this.deleting = true;
        locationsStore.dispatch(CrudAction.Delete, { id: this.id });
        this.subscribe(locationsStore, CrudReponse.Delete)
            .then((e: any) => {
                this.deleting = false;
                this.deleteDialog = false;
                this.close();
            })
            .catch((e: any) => {
                this.deleting = false;
            });
    }

    private load(): Promise<any> {
        if (this.id != Guid.EmptyGuid()) {
            this.loading = true;
            return new Promise((resolve, reject) => {
                this.loadLocation()
                    .then((e) => {
                        this.loading = false;
                        resolve(e);
                    })
                    .catch((e: any) => {
                        reject(e);
                    });
            });
        } else {
            this.location = {
                id: Guid.EmptyGuid(),
                code: '',
                name: '',
            } as LocationModel;
            return new Promise((resolve, reject) => {
                resolve(null);
            });
        }
    }

    private loadLocation() {
        return new Promise((resolve, reject) => {
            locationsStore.dispatch(CrudAction.Get, { id: this.id });
            this.subscribe(locationsStore, CrudReponse.Get)
                .then((e) => {
                    this.location = e;
                    this.inheritMaintenance = this.location!.hourlyMaintenanceCosts == null;
                    this.inheritDowntime = this.location!.hourlyDowntimeCosts == null;
                    this.inheritLostProfit = this.location!.hourlyDowntimeLostProfit == null;
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    private save() {
        if (this.form.validate()) {
            const location = { ...this.location } as LocationModel;
            if (this.hasParental) {
                location.hourlyMaintenanceCosts = this.inheritMaintenance ? null : location.hourlyMaintenanceCosts;
                location.hourlyDowntimeCosts = this.inheritDowntime ? null : location.hourlyDowntimeCosts;
                location.hourlyDowntimeLostProfit = this.inheritLostProfit ? null : location.hourlyDowntimeLostProfit;
            }
            if (!location.canPlanShifts) {
                location.locationGroups = [];
            }
            this.saving = true;
            if (this.id == Guid.EmptyGuid()) {
                locationsStore.dispatch(CrudAction.Create, { item: location });
                this.subscribe(locationsStore, CrudReponse.Create)
                    .then(() => {
                        this.load().then(() => {
                            this.saving = false;
                            this.close();
                        });
                    })
                    .catch((e) => {
                        this.saving = false;
                        throw e;
                    });
            } else {
                locationsStore.dispatch(CrudAction.Update, { item: location });
                this.subscribe(locationsStore, CrudReponse.Update)
                    .then(() => {
                        this.load().then(() => {
                            this.saving = false;
                            this.close();
                        });
                    })
                    .catch((e) => {
                        this.saving = false;
                        throw e;
                    });
            }
        }
    }

    get hasParental(): boolean {
        return !!this.location?.parentalLocationId;
    }

    positiveNumberRule(v: any) {
        return (!!v && v > 0) || (this.$t('validation.enterAnInteger') as any);
    }
}
